import React from 'react'
import { TouchableOpacity, ViewStyle, TextStyle, View, StyleProp } from 'react-native'
import { useTheme } from '@ui-kitten/components'
import { StyleService, useStyleSheet } from '@src/style/service'
import { IconName, Icon, IconButton } from '@components/base'
import { Text } from '@components/base'

interface TouchableSectionProps {
  title: string
  subtitle?: string
  labelStyle?: StyleProp<TextStyle>
  subtitleStyle?: StyleProp<TextStyle>
  style?: StyleProp<ViewStyle>
  iconName?: IconName
  tagComponent?: React.ReactNode
  accessoryLeft?: React.ReactNode
  showRightArrow?: boolean
  rightArrowColor?: string
  children?: React.ReactNode
  onPress?: () => void
  onDismiss?: () => void
  disabled?: boolean
}

export const TouchableSection = (props: TouchableSectionProps) => {
  const {
    style,
    labelStyle,
    subtitleStyle,
    subtitle,
    iconName,
    title,
    children,
    showRightArrow = false,
    rightArrowColor,
    onPress,
    onDismiss,
    disabled = false,
    tagComponent,
    accessoryLeft,
  } = props
  const styles = useStyleSheet(themedStyle)
  const theme = useTheme()

  let leftAccessory = null

  if (accessoryLeft) {
    leftAccessory = accessoryLeft
  } else if (iconName) {
    leftAccessory = <Icon name={iconName} style={styles.icon} />
  }

  return (
    <TouchableOpacity
      disabled={disabled}
      style={[styles.container, subtitle && { alignItems: 'flex-start' }, style]}
      activeOpacity={onPress ? 0.65 : 1}
      accessibilityLabel={title}
      onPress={onPress}
    >
      <View style={[styles.contentContainer, subtitle && { alignItems: 'flex-start' }]}>
        {leftAccessory}
        <View style={styles.textContainer}>
          <Text type="regular" bold lineSpacing="tight" style={[styles.titleLabel, labelStyle]}>
            {title}
          </Text>
          {!!subtitle && (
            <Text type="regular" lineSpacing="tight" style={[styles.subtitle, subtitleStyle]}>
              {subtitle}
            </Text>
          )}
        </View>
      </View>
      {children}
      <View style={styles.row}>
        {tagComponent}
        {showRightArrow && !onDismiss && (
          <Icon name="caret-right" color={rightArrowColor || theme['theme.primary.base']} />
        )}
        {onDismiss && (
          <IconButton
            type="transparent"
            size="s"
            iconName="x"
            onPress={onDismiss}
            accessibilityLabel="Dismiss"
          />
        )}
      </View>
    </TouchableOpacity>
  )
}

const themedStyle = StyleService.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  contentContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  textContainer: {
    flex: 1,
  },
  icon: {
    marginRight: 12,
  },
  titleLabel: {
    minWidth: 128,
  },
  subtitle: {
    marginTop: 4,
  },
})
